import {useEffect, useId, useRef, useState} from 'react';
import * as Popover from '@radix-ui/react-popover';
import {Form} from 'react-bootstrap';
import {FaBell} from 'react-icons/fa';
import {useChat} from '../ChatProvider';
import {chatErrorMessage} from '../chatUtils';
import useChannelUnread from './useChannelUnread';

const STATUS_LABELS = {
    checking: 'Checking notifications…',
    blocked: 'Notifications are blocked in your browser settings.',
    unsupported: 'This browser does not support push notifications.',
    unavailable: 'Push notifications are currently unavailable.',
};

export default function ChatNotificationButton({conversationId, onNavigate}) {
    const {notifications, channelUnreadRevision, connectionStatus} = useChat();
    const unread = useChannelUnread(conversationId, channelUnreadRevision, connectionStatus);
    const [open, setOpen] = useState(false);
    const [navigating, setNavigating] = useState(false);
    const [navigationError, setNavigationError] = useState(null);
    const mounted = useRef(true);
    useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
    const toggleId = useId();
    const status = notifications?.status || 'unavailable';
    const label = unread.count === null ? 'Unread messages' : `${unread.count} unread messages`;
    const select = async item => {
        setOpen(false);
        setNavigating(true);
        setNavigationError(null);
        try {
            const success = await onNavigate?.(item);
            if (!mounted.current) return;
            if (!success) {
                setNavigationError('This message could not be opened. Please retry or refresh the list.');
                setOpen(true);
                void unread.refresh();
            }
        } catch (error) {
            if (!mounted.current) return;
            setNavigationError(chatErrorMessage(error));
            setOpen(true);
            void unread.refresh();
        } finally { if (mounted.current) setNavigating(false); }
    };
    return <Popover.Root open={open} onOpenChange={value => {setOpen(value); if (value) void unread.refresh();}}>
        <Popover.Trigger asChild>
            <button type="button" className="chat-notification-bell" aria-label={label} title={label} disabled={navigating}>
                <FaBell aria-hidden="true"/>
                {unread.count > 0 ? <span className="chat-notification-count" aria-hidden="true">{unread.count}</span> : null}
            </button>
        </Popover.Trigger>
        <Popover.Portal>
            <Popover.Content className="chat-notification-menu" align="end" sideOffset={8} collisionPadding={12} aria-label="Channel notifications">
                <div className="chat-notification-setting">
                    <Form.Check id={toggleId} type="switch" role="switch" label="Enable notifications" checked={status === 'enabled'}
                        disabled={Boolean(STATUS_LABELS[status])}
                        onChange={() => status === 'enabled' ? notifications.disable() : notifications.enable()}/>
                    {STATUS_LABELS[status] ? <p role="status">{STATUS_LABELS[status]}</p> : null}
                    {notifications?.error ? <p role="alert">{chatErrorMessage(notifications.error)}</p> : null}
                </div>
                {navigationError ? <p className="chat-notification-status" role="alert">{navigationError}</p> : null}
                {unread.error ? <div className="chat-notification-status" role="alert">{chatErrorMessage(unread.error)} <button type="button" onClick={unread.refresh}>Retry</button></div> : null}
                {unread.loading ? <p className="chat-notification-status" role="status">Loading unread messages…</p> : null}
                {!conversationId ? <p className="chat-notification-status">Select a conversation.</p> : null}
                {conversationId && unread.count === 0 && !unread.loading && !unread.error ? <p className="chat-notification-status">No unread messages</p> : null}
                <ul className="chat-notification-list" aria-label="Unread messages">
                    {unread.items.map(item => <li key={item.id}>
                        <button type="button" className="chat-notification-item" onClick={() => select(item)}>
                            <span className="chat-notification-byline"><strong>{item.sender_name}</strong><time dateTime={item.created_at}>{new Date(item.created_at).toLocaleString(undefined, {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</time></span>
                            {item.thread_root_message_id ? <span className="chat-notification-thread">Thread: {item.root_preview}</span> : null}
                            <span className="chat-notification-preview">{item.preview}</span>
                        </button>
                    </li>)}
                </ul>
                {unread.next ? <button className="chat-notification-more" type="button" disabled={unread.loading} onClick={unread.loadMore}>Load more</button> : null}
            </Popover.Content>
        </Popover.Portal>
    </Popover.Root>;
}
